import {useEffect} from 'react';
import L from 'leaflet';
import {Circle, CircleMarker, MapContainer, TileLayer, useMap} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ATTENDANCE_MAP_TILES_ATTRIBUTION,
  ATTENDANCE_MAP_TILES_URL,
} from '../../utils/constants/attendance-tracker';

type AttendanceLocationMapProps = {
  latitude: number;
  longitude: number;
  /** Radius of uncertainty, in meters */
  accuracy: number;
  /** If the map can be moved and zoomed. Fixed maps do not get in the way when scrolling the page. */
  interactive?: boolean;
  /** Height of the map, in pixels */
  height?: number;
};

/** Keeps the map centered on the position and zoomed to its accuracy circle */
const FitToPosition = ({
  latitude,
  longitude,
  accuracy,
}: Pick<AttendanceLocationMapProps, 'latitude' | 'longitude' | 'accuracy'>) => {
  const map = useMap();

  // Follow the position
  useEffect(() => {
    map.setView([latitude, longitude], map.getZoom(), {animate: false});
  }, [map, latitude, longitude]);

  // Zoom to the accuracy circle only when the accuracy changes noticeably
  const accuracyStep = Math.round(accuracy / 5);
  useEffect(() => {
    const radius = Math.max(accuracy, 10);
    map.fitBounds(L.latLng(latitude, longitude).toBounds(radius * 2), {
      maxZoom: 19,
      animate: false,
      padding: [20, 20],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, accuracyStep]);

  return null;
};

/** Updates the map when its container changes size (e.g. when it is in a dialog that is still opening) */
const InvalidateSizeOnResize = () => {
  const map = useMap();

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
};

/** Shows an exact position and the circle of its accuracy on a street map */
export const AttendanceLocationMap = ({
  latitude,
  longitude,
  accuracy,
  interactive = true,
  height = 280,
}: AttendanceLocationMapProps) => {
  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={18}
      scrollWheelZoom={false}
      dragging={interactive}
      touchZoom={interactive}
      doubleClickZoom={interactive}
      zoomControl={interactive}
      style={{height: `${height}px`, width: '100%', borderRadius: '8px', zIndex: 0}}
    >
      <TileLayer url={ATTENDANCE_MAP_TILES_URL} attribution={ATTENDANCE_MAP_TILES_ATTRIBUTION} />
      <Circle center={[latitude, longitude]} radius={accuracy} />
      <CircleMarker center={[latitude, longitude]} radius={7} />
      <FitToPosition latitude={latitude} longitude={longitude} accuracy={accuracy} />
      <InvalidateSizeOnResize />
    </MapContainer>
  );
};
